import React from 'react';

/**
 * Creates HTML table headers that display the column name and the sort glyphs. Also contains the callback
 * for when the header is select to be sorted.
 * @param {string} displayName The column header displayName
 * @param {string} sortName The unique value given to each header on which to sort by
 * @param {string} sortBy The column value (sortName) of which to sort by
 * @param {boolean} sortAsc Determine whether to sort ascending or descending
 * @param {function} changeSort sort action that is called when header is clicked
 */
export function buildSortableColumn(displayName, sortName, sortBy, sortAsc, changeSort) {
  var selected = sortBy === sortName;
  var icon = selected ? sortAsc ? "glyphicon glyphicon-arrow-down" :
                                  "glyphicon glyphicon-arrow-up" :
                        "";
  return (
    <th scope="col"
      aria-sort={ selected ? sortAsc ? "ascending" : "descending" : ''}
      className={`sortable sorted`}
      key={displayName}
      onClick={evt => handleColumnSelect(sortName, changeSort)}>
      {displayName}
      <span className={icon} />
    </th>
  );
}

/**
 * Function that creates an array of sortable headers(th) using the given parameters.
 * @param {Map<string, object>} headerNames The map that contains the displayName, sortName, and type
 *    for the given headers. Must follow the form:
 *
 *    const headerNames = new Map([
      ['date', {
         displayName: 'Date Taken',
         dataType: 'date' }],
      ['touchpoint', {
        displayName: 'Touchpoint',
        dataType: 'alpha' }]);
 * @param {string} sortBy The string that identifies the column to sort.
 * @param {boolean} sortAsc If true, then sorts ascending, if false, then descending.
 * @param {function} changeSort Callback function to handle the sorting of the data.
 */
export function buildSortHeader(headerNames, sortBy, sortAsc, changeSort) {
  let headerValues = [];
  headerNames.forEach((value, key, map) => {
    headerValues.push(buildSortableColumn(value.displayName, key, sortBy, sortAsc, changeSort));
  });
  return headerValues;
}

/**
 * Function that returns the trailing text that describes what field is being sorted
 * and the method of how it is being sorted.
 * @param {Map<string, object>} headerNames The map that contains the displayName, sortName, and type
 *    for the given headers. Must follow the form:
 *
 *    const headerNames = new Map([
      ['date', {
         displayName: 'Date Taken',
         dataType: 'date' }],
      ['touchpoint', {
        displayName: 'Touchpoint',
        dataType: 'alpha' }]);
 * @param {string} sortBy The string that identifies the column to sort.
 * @param {boolean} sortAsc If true, then sorts ascending, if false, then descending.
 */
export function getSortText(headerNames, sortBy, sortAsc) {
  const header = headerNames.get(sortBy);
  if(header === undefined || header === null) return '';
  const headerSortName = (header.sortName !== undefined && header.sortName !== null) ? header.sortName : header.displayName;
  switch (header.dataType) {
    case 'alpha':
      if(sortAsc) return headerSortName + ' alphabetically ';
      else return headerSortName + ' reverse alphabetically ';
    case 'numeric':
      if(sortAsc) return headerSortName + ' numerically ';
      else return headerSortName + ' reverse numerically ';
    case 'date':
      if(sortAsc) return headerSortName + ' chronologically ';
      else return headerSortName + ' reverse chronologically ';
    default:
      throw new Error('No type found for type: ' + header.dataType);
  }
}

/**
 * Function that acts as a filter and makes sure that only a mouseclick or the user hitting 'enter'
 * triggers a sort.
 * @param {string} sortName The unique value given to each header on which to sort by
 * @param {function} changeSort sort action that is called when header is clicked
 */
function handleColumnSelect(sortName, changeSort) {
    changeSort(sortName);
}

/**
 * Function that converts a single api call state and a paging information into props
 * needed for rendering a PagedList
 * @param {ApiCallState} state the state of the api call that fetches the list
 * @param {PagingState} pagingData paging information
 */
export function getPagedListProps(state, pagingData, actions) {
  const { itemsPerPage } = pagingData || {};
  const { isFetching, err, data } = state || {};
  const { changePageNumber, changeResultsPerPage } = actions;

  var items = [];
  var currentPage = 1;
  var totalItemsCount = 0;
  if(data) {
    items = data.results;
    currentPage = data.currentPage;
    totalItemsCount = data.totalResults;
  }
  return {
    items,
    currentPage,
    resultsPerPage: itemsPerPage,
    totalItemsCount,
    changePageNumber,
    changeResultsPerPage,
    isLoading: isFetching,
    loadingErr: err,
  }
}
